import { useEffect, useRef, useState } from 'react';
import type { AnalyticsEvent, PlannerAction } from '../../core/events';
import { call, courseLabel, localToday, planPath, toApiError, type GetToken, type PlanListResponse, type PlanSummary } from '../api';
import { hasMyWeek, homeFor, planbookOn } from '../../core/home';
import { landingHref, linkTo, MY_WEEK_HREF, navigate } from '../navigation';
import { ConfirmDelete } from './ConfirmDelete';

/**
 * The teacher's plans, last opened first, with New plan and delete (R18). As home, it shows a
 * loading state until `homeFor` decides, then opens My week or the plan opened last (R4, R13);
 * the list stays one link away. As the list, it links to My week (R7), named Planbook with day
 * items on, when a built plan opens on the planbook at this week instead of its weekly view.
 */
export function PlanList({ getToken, onTrack, onNew, openLast = false, myWeek = false, dayItems = false }: {
  getToken: GetToken; onNew(): void; openLast?: boolean; myWeek?: boolean; dayItems?: boolean;
  onTrack(action: PlannerAction, properties?: AnalyticsEvent['properties']): void;
}) {
  const [list, setList] = useState<PlanListResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [deleting, setDeleting] = useState<PlanSummary | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const viewed = useRef(false);
  const trackRef = useRef(onTrack); trackRef.current = onTrack;

  useEffect(() => {
    let live = true; const controller = new AbortController(); setFailed(false);
    void call<PlanListResponse>(getToken, '/api/plans', { signal: controller.signal })
      .then(data => {
        if (!live) return;
        const home = openLast ? homeFor(data.plans, myWeek, dayItems) : { kind: 'list' as const };
        if (home.kind === 'my_week') { navigate(MY_WEEK_HREF, true); return; }
        if (home.kind === 'plan') { navigate(landingHref(home.plan), true); return; }
        setList(data);
        // One event per view of the list, however often it reloads behind it.
        if (!viewed.current) { viewed.current = true; trackRef.current('plan_list_viewed', { plan_count: data.plans.length }); }
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; controller.abort(); };
  }, [getToken, attempt, openLast, myWeek, dayItems]);

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true); setDeleteError(null);
    try {
      await call(getToken, planPath(deleting.id), { method: 'DELETE' });
      setList(l => l && { ...l, plans: l.plans.filter(p => p.id !== deleting.id) });
      setDeleting(null);
    } catch (failure) {
      setDeleteError(toApiError(failure).message);
    } finally { setDeleteBusy(false); }
  }

  useEffect(() => { document.title = 'Your plans · 240 Planner'; }, []);
  const full = list !== null && list.plans.length >= list.limit;
  // Home never draws the list or a plan before it has decided where to go (R4).
  if (openLast && !list && !failed) return <p className="notice" role="status">Opening your planner…</p>;
  const showMyWeek = list !== null && hasMyWeek(list.plans.filter(p => p.built).length, myWeek, dayItems);
  const planbookToday = planbookOn(myWeek, dayItems) ? localToday() : null;
  return <section className="plan-list" aria-labelledby="plan-list-title">
    <div className="page-heading">
      <div><div className="eyebrow">240 PLANNER</div><h1 id="plan-list-title" className="setup-title">Your plans</h1></div>
      <div className="page-actions">
        {showMyWeek && <a className="secondary button-link" {...linkTo(MY_WEEK_HREF)}>{dayItems ? 'Planbook' : 'My week'}</a>}
        <button className="primary" type="button" disabled={!list || full} aria-describedby={full ? 'plan-limit-note' : undefined} onClick={onNew}>New plan</button>
        {full && <p id="plan-limit-note" className="muted plan-limit-note">You can keep five plans. Delete one to start another.</p>}
      </div>
    </div>
    {!list && !failed && <p role="status" className="muted">Loading your plans…</p>}
    {failed && <div className="notice" role="alert"><p>We could not load your plans.</p><button className="secondary" type="button" onClick={() => setAttempt(n => n + 1)}>Try again</button></div>}
    {list && list.plans.length === 0 && <div className="notice"><h2>Plan your year</h2><p>Pick your course and your school calendar, then draft a year of units from the official standards or start blank.</p></div>}
    {list && list.plans.length > 0 && <ul className="plan-cards">{list.plans.map(plan => {
      const label = courseLabel(plan.course);
      return <li key={plan.id} className="plan-card">
        <a className="plan-card-link" {...linkTo(landingHref(plan, planbookToday))}>
          <strong>{label}</strong>
          <small>{plan.built ? 'Built' : 'Not built yet'} · Updated {new Date(plan.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</small>
        </a>
        <button className="text-button danger" type="button" aria-label={`Delete ${label}`} onClick={() => { setDeleteError(null); setDeleting(plan); }}>Delete</button>
      </li>;
    })}</ul>}
    {deleting && <ConfirmDelete name={courseLabel(deleting.course)} busy={deleteBusy} error={deleteError} onConfirm={() => void confirmDelete()} onCancel={() => setDeleting(null)} />}
  </section>;
}
